import api from "./axios";

export interface Assignment {
    id: string;
    class_subject_id: string;
    teacher_id: string;
    title: string;
    description?: string;
    instructions?: string;
    weight: number;
    max_score: number;
    deadline: string;
    published_at?: string;
    allow_late_submission: boolean;
    late_penalty_percentage: number;
    is_published: boolean;
    created_at: string;
    updated_at: string;

    // Relationships
    class_subject?: {
        id: string;
        class?: {
            id: string;
            name: string;
            grade_level: string;
        };
        subject?: {
            id: string;
            name: string;
            code: string;
        };
    };

    teacher?: {
        id: string;
        nip: string;
        user?: {
            id: string;
            name: string;
            email: string;
        };
    };

    // Computed attributes
    is_overdue?: boolean;
    days_until_deadline?: number;
    submitted_at?: string;
    has_attachment?: boolean;
    submissions_count?: number;
    graded_submissions_count?: number;
    pending_submissions_count?: number;
}

export interface AssignmentSubmission {
    id: string;
    assignment_id: string;
    student_id: string;
    student_name?: string;
    answer_text?: string;
    file_path?: string;
    file_name?: string;
    file_size?: number;
    mime_type?: string;
    score?: number;
    feedback?: string;
    graded_by?: string;
    graded_at?: string;
    submitted_at?: string;
    status: "draft" | "submitted" | "graded" | "returned";
    is_late: boolean;
    created_at: string;
    updated_at: string;

    // Relationships
    assignment?: {
        id: string;
        title: string;
        description?: string;
        instructions?: string;
        max_score: number;
        deadline: string;
        allow_late_submission: boolean;
        late_penalty_percentage: number;
        class_subject?: {
            id: string;
            class?: {
                id: string;
                name: string;
                grade_level: string;
            };
            subject?: {
                id: string;
                name: string;
                code: string;
            };
        };
    };

    student?: {
        id: string;
        nisn: string;
        name?: string;
        email?: string;
    };

    graded_by_teacher?: {
        id: string;
        nip: string;
        name?: string;
        email?: string;
    };

    // Computed attributes
    file_url?: string;
    file_size_formatted?: string;
    status_display?: string;
    is_graded?: boolean;
    is_submitted?: boolean;
}

export interface CreateAssignmentData {
    class_subject_id: string;
    teacher_id?: string;
    title: string;
    description?: string;
    instructions?: string;
    weight: number;
    max_score: number;
    deadline: string;
    allow_late_submission?: boolean;
    late_penalty_percentage?: number;
    is_published?: boolean;
}

export interface UpdateAssignmentData {
    class_subject_id?: string;
    teacher_id?: string;
    title?: string;
    description?: string;
    instructions?: string;
    weight?: number;
    max_score?: number;
    deadline?: string;
    allow_late_submission?: boolean;
    late_penalty_percentage?: number;
    is_published?: boolean;
}

export interface SubmitAssignmentData {
    answer_text?: string;
    file?: File;
    submit_now?: boolean;
}

export interface GradeSubmissionData {
    score: number;
    feedback?: string;
    return_to_student?: boolean;
}

export interface AssignmentFilters {
    search?: string;
    status?: "published" | "draft";
    class_id?: string;
    subject_id?: string;
    teacher_id?: string;
    class_subject_id?: string;
    page?: number;
    per_page?: number;
}

export interface SubmissionFilters {
    status?: "draft" | "submitted" | "graded" | "returned";
    is_late?: boolean;
    search?: string;
    page?: number;
    per_page?: number;
}

export const assignmentApi = {
    // Teacher/Admin endpoints
    async getAll(filters: AssignmentFilters = {}): Promise<{
        success: boolean;
        message: string;
        data: { items: Assignment[]; meta: any };
    }> {
        const response = await api.get("/v1/assignments", { params: filters });
        return response.data;
    },

    async getById(id: string): Promise<Assignment> {
        const response = await api.get(`/v1/assignments/${id}`);
        return response.data.data;
    },

    async create(data: CreateAssignmentData): Promise<Assignment> {
        const response = await api.post("/v1/assignments", data);
        return response.data.data;
    },

    async update(id: string, data: UpdateAssignmentData): Promise<Assignment> {
        const response = await api.put(`/v1/assignments/${id}`, data);
        return response.data.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/v1/assignments/${id}`);
    },

    async publish(id: string): Promise<Assignment> {
        const response = await api.post(`/v1/assignments/${id}/publish`);
        return response.data.data;
    },

    async unpublish(id: string): Promise<Assignment> {
        const response = await api.post(`/v1/assignments/${id}/unpublish`);
        return response.data.data;
    },

    async getStatistics(id: string): Promise<any> {
        const response = await api.get(`/v1/assignments/${id}/statistics`);
        return response.data.data;
    },

    async getSubmissions(
        id: string,
        filters: SubmissionFilters = {},
    ): Promise<{
        success: boolean;
        message: string;
        data: { items: AssignmentSubmission[]; meta: any };
    }> {
        const response = await api.get(`/v1/assignments/${id}/submissions`, {
            params: filters,
        });
        return response.data;
    },

    // Student endpoints
    async getStudentAssignments(): Promise<Assignment[]> {
        const response = await api.get("/v1/student-assignments");
        return response.data.data || [];
    },

    async getStudentAssignment(id: string): Promise<Assignment> {
        const response = await api.get(`/v1/student-assignments/${id}`);
        return response.data.data;
    },

    async submitAssignment(
        id: string,
        data: SubmitAssignmentData,
    ): Promise<AssignmentSubmission> {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
            if (key === "file" && data.file instanceof File) {
                formData.append("file", data.file);
            } else if (data[key as keyof SubmitAssignmentData] !== undefined) {
                formData.append(
                    key,
                    String(data[key as keyof SubmitAssignmentData]),
                );
            }
        });

        const response = await api.post(
            `/v1/student-assignments/${id}/submit`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        );
        return response.data.data;
    },

    async getStudentSubmission(id: string): Promise<AssignmentSubmission> {
        const response = await api.get(
            `/v1/student-assignments/${id}/submission`,
        );
        return response.data.data;
    },

    async downloadSubmissionFile(id: string): Promise<Blob> {
        const response = await api.get(
            `/v1/student-assignments/${id}/download`,
            {
                responseType: "blob",
            },
        );
        return response.data;
    },

    // Grading endpoints
    async getGradingSubmissions(
        assignmentId: string,
        filters: SubmissionFilters = {},
    ): Promise<{
        success: boolean;
        message: string;
        data: { items: AssignmentSubmission[]; meta: any };
    }> {
        const response = await api.get(
            `/v1/assignment-grading/assignment/${assignmentId}/submissions`,
            { params: filters },
        );
        return response.data;
    },

    async getGradingSubmission(
        submissionId: string,
    ): Promise<AssignmentSubmission> {
        const response = await api.get(
            `/v1/assignment-grading/submissions/${submissionId}`,
        );
        return response.data.data;
    },

    async gradeSubmission(
        submissionId: string,
        data: GradeSubmissionData,
    ): Promise<AssignmentSubmission> {
        const response = await api.post(
            `/v1/assignment-grading/submissions/${submissionId}/grade`,
            data,
        );
        return response.data.data;
    },

    async getPendingSubmissions(
        assignmentId: string,
    ): Promise<AssignmentSubmission[]> {
        const response = await api.get(
            `/v1/assignment-grading/assignment/${assignmentId}/pending`,
        );
        return response.data.data || [];
    },

    async getGradedSubmissions(
        assignmentId: string,
    ): Promise<AssignmentSubmission[]> {
        const response = await api.get(
            `/v1/assignment-grading/assignment/${assignmentId}/graded`,
        );
        return response.data.data || [];
    },

    async getLateSubmissions(
        assignmentId: string,
    ): Promise<AssignmentSubmission[]> {
        const response = await api.get(
            `/v1/assignment-grading/assignment/${assignmentId}/late`,
        );
        return response.data.data || [];
    },

    async getGradingStatistics(assignmentId: string): Promise<any> {
        const response = await api.get(
            `/v1/assignment-grading/assignment/${assignmentId}/statistics`,
        );
        return response.data.data;
    },

    async deleteSubmission(submissionId: string): Promise<void> {
        await api.delete(`/v1/assignments/submissions/${submissionId}`);
    },
};

export default assignmentApi;
